'use client'
import Image from 'next/image'
import Link from 'next/link'
import LinkComponent from '@components/LinkComponent'
import { AiFillInstagram, AiFillFacebook, AiFillLinkedin, AiFillYoutube } from 'react-icons/ai'
import { FaTiktok } from 'react-icons/fa'
import { FaSquareThreads, FaXTwitter } from 'react-icons/fa6'

// ─── RTL helpers ──────────────────────────────────────────────────────────────

const RTL_LANGS  = new Set(['he', 'ar', 'fa', 'ur'])
const isRTL      = (lang) => RTL_LANGS.has(lang)

const ALIGN_MAP      = { left: 'start', right: 'end', center: 'center', start: 'start', end: 'end' }
const normalizeAlign = (align) => ALIGN_MAP[align] ?? 'start'
const toAlignItems   = (align) =>
  align === 'center' ? 'center' : align === 'end' ? 'flex-end' : 'flex-start'

// ─── Social icon component ────────────────────────────────────────────────────

const SOCIAL_LABEL = {
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok',
  linkedin: 'LinkedIn', youtube: 'YouTube', twitter: 'Twitter', threads: 'Threads',
}

function SocialIcon({ platform, size, color }) {
  const props = { size, color, style: { display: 'block' } }
  switch (platform) {
    case 'facebook':  return <AiFillFacebook  {...props} />
    case 'instagram': return <AiFillInstagram {...props} />
    case 'tiktok':    return <FaTiktok        {...props} />
    case 'linkedin':  return <AiFillLinkedin  {...props} />
    case 'youtube':   return <AiFillYoutube   {...props} />
    case 'threads':   return <FaSquareThreads {...props} />
    case 'twitter':   return <FaXTwitter      {...props} />
    default:          return null
  }
}

function SocialButton({ platform, socialStyle, iconColor, bgColor, iconSize }) {
  const label = SOCIAL_LABEL[platform] ?? platform
  const size  = iconSize ?? 24
  const color = iconColor ?? '#ffffff'
  const bg    = bgColor ?? 'rgba(255,255,255,0.12)'
  const icon  = <SocialIcon platform={platform} size={size} color={color} />

  if (socialStyle === 'icon+label') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6, color, fontSize:size*0.58, fontWeight:500 }}>
      {icon}{label}
    </span>
  )
  if (socialStyle === 'pill') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6, background:bg, color,
      borderRadius:999, padding:`4px ${Math.round(size*0.5)}px`, fontSize:size*0.55, fontWeight:600 }}>
      <SocialIcon platform={platform} size={Math.round(size*0.85)} color={color} />{label}
    </span>
  )
  if (socialStyle === 'square') return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center',
      background:bg, borderRadius:7, width:Math.round(size*1.55), height:Math.round(size*1.55) }}>
      {icon}
    </span>
  )
  if (socialStyle === 'outline') return (
    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center',
      border:`1.5px solid ${color}`, borderRadius:7,
      width:Math.round(size*1.55), height:Math.round(size*1.55), opacity:0.85 }}>
      {icon}
    </span>
  )
  return icon
}

// ─── Resolve background CSS ───────────────────────────────────────────────────

const resolveBg = (footer) => {
  const bgColor  = footer.bgColor  ?? '#1f2937'
  const bgColor2 = footer.bgColor2 ?? '#111827'
  if (footer.bgType === 'gradient') {
    return `linear-gradient(${footer.bgGradientAngle ?? 135}deg, ${bgColor}, ${bgColor2})`
  }
  return bgColor
}

// ─── Company column ───────────────────────────────────────────────────────────

function CompanyColumn({ footer, lang, l, align = 'start' }) {
  const companyColor   = footer.companyTextColor   ?? '#ffffff'
  const companySize    = footer.companyTextSize    ?? '0.875rem'
  const companyFw      = footer.companyTextFw      ?? '400'
  const companySpacing = footer.companyTextSpacing ?? '0.35rem'
  const iconColor      = footer.socialIconColor    ?? '#ffffff'
  const iconSize       = footer.socialIconSize     ?? 24
  const bgColor        = footer.socialBgColor      ?? 'rgba(255,255,255,0.12)'

  const normalAlign = normalizeAlign(align)
  const companyName = footer.company?.name?.[l] || footer.company?.name?.en || 'Home'

  return (
    <div className="footer-company-col" style={{
      display: 'flex', flexDirection: 'column', gap: companySpacing,
      textAlign: normalAlign,
      alignItems: toAlignItems(normalAlign),
    }}>
      {footer.showLogo !== false && (
        <div style={{ marginBottom: 8 }}>
          {/* Wrap logo in a link back to home with a descriptive aria-label */}
          <Link href="/" aria-label={`${companyName} — Go to homepage`}>
            <Image
              src={footer.logoUrl || '/logo.png'}
              alt={companyName}
              width={footer.logoWidth ?? 160}
              height={footer.logoHeight ?? 48}
              style={{ objectFit:'contain', width:'auto', maxWidth:footer.logoWidth??160, height:footer.logoHeight??48 }}
            />
          </Link>
        </div>
      )}

      {[
        footer.company?.name?.[l]     || footer.company?.name?.en,
        footer.company?.address1?.[l] || footer.company?.address1?.en,
        footer.company?.address2?.[l] || footer.company?.address2?.en,
        footer.company?.address3?.[l] || footer.company?.address3?.en,
        footer.company?.phone1,
        footer.company?.phone2,
        footer.company?.email,
      ].filter(Boolean).map((line, i) => (
        <p key={i} style={{ margin:0, fontSize:companySize, fontWeight:companyFw, color:companyColor, lineHeight:1.5 }}>
          {line}
        </p>
      ))}

      {footer.socialLinks?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {(footer.followUsLabel?.[l] || footer.followUsLabel?.en) && (
            <p style={{ margin:'0 0 6px', fontSize:'0.75rem', fontWeight:700, color:companyColor, opacity:0.7 }}>
              {footer.followUsLabel?.[l] || footer.followUsLabel?.en}
            </p>
          )}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
            {footer.socialLinks.map((social, i) => {
              const platformLabel = SOCIAL_LABEL[social.platform] ?? social.platform
              return (
                <Link
                  key={i}
                  href={social.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  // aria-label gives screen readers "Visit us on Facebook" etc.
                  aria-label={`Visit us on ${platformLabel}`}
                  style={{ display:'flex', alignItems:'center', textDecoration:'none', color:iconColor }}
                >
                  {/* Icons are decorative — the link's aria-label is the name */}
                  <span aria-hidden="true">
                    <SocialButton
                      platform={social.platform}
                      socialStyle={footer.socialStyle ?? 'icon'}
                      iconColor={iconColor}
                      bgColor={bgColor}
                      iconSize={iconSize}
                    />
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Links column ─────────────────────────────────────────────────────────────

function LinksColumn({ col, footer, l }) {
  const titleColor = footer.sectionTitleColor ?? '#ffffff'
  const titleSize  = footer.sectionTitleSize  ?? '0.875rem'
  const titleFw    = footer.sectionTitleFw    ?? '700'
  const linkColor  = footer.sectionLinkColor  ?? '#9ca3af'
  const linkSize   = footer.sectionLinkSize   ?? '0.875rem'
  const linkFw     = footer.sectionLinkFw     ?? '400'
  const linkHover  = footer.sectionLinkHover  ?? 'var(--color-main)'
  const spacing    = footer.sectionSpacing    ?? '0.4rem'

  return (
    <ul style={{ listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:spacing }}>
      {(col.title?.[l] || col.title?.en) && (
        <li style={{ fontSize:titleSize, fontWeight:titleFw, color:titleColor, marginBottom:4 }}>
          {col.title?.[l] || col.title?.en}
        </li>
      )}
      {(col.links ?? []).map((link, li) => (
        <li key={link.id ?? li}>
          <LinkComponent
            href={link.href || '#'}
            style={{ fontSize:linkSize, fontWeight:linkFw, color:linkColor, textDecoration:'none', transition:'color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = linkHover }}
            onMouseLeave={e => { e.currentTarget.style.color = linkColor }}
          >
            {link.label?.[l] || link.label?.en || ''}
          </LinkComponent>
        </li>
      ))}
    </ul>
  )
}

// ─── FooterDefault ────────────────────────────────────────────────────────────

const FooterDefault = ({ footer, dictionary, lang }) => {
  if (!footer) return null

  const l           = lang || 'en'
  const bgCss       = resolveBg(footer)
  const borderTop   = footer.borderTop !== false
  const borderColor = footer.borderColor ?? 'var(--color-main)'
  const companyName = footer.company?.name?.[l] || footer.company?.name?.en || ''
  const rtl         = isRTL(l)

  const copyrightColor       = footer.copyrightTextColor  ?? '#6b7280'
  const copyrightSize        = footer.copyrightTextSize   ?? '0.75rem'
  const copyrightBorder      = footer.copyrightBorderTop  !== false
  const copyrightBorderColor = footer.copyrightBorderColor ?? 'rgba(255,255,255,0.1)'

  const columns = footer.columns?.length
    ? footer.columns
    : [
        { id: 'company', type: 'company' },
        ...(footer.sections ?? []).map(s => ({ ...s, type: 'links' })),
      ]

  return (
    <>
      <style>{`
        .footer-columns-grid {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
        }
        .footer-col {
          flex: 0 0 auto;
        }
        @media (max-width: 767px) {
          .footer-columns-grid {
            flex-direction: column !important;
            gap: 24px !important;
          }
          .footer-col {
            flex: 0 0 100% !important;
            width: 100% !important;
            min-width: 0 !important;
            text-align: start !important;
          }
          .footer-company-col {
            width: 100%;
            align-items: flex-start !important;
          }
        }
      `}</style>

      <footer
        dir={rtl ? 'rtl' : 'ltr'}
        style={{ background: bgCss, borderTop: borderTop ? `3px solid ${borderColor}` : 'none' }}
      >
        <div className="container-wide mx-auto" style={{ padding:`${footer.paddingY ?? 36}px ${footer.paddingX ?? 0}px 0` }}>

          <div className="footer-columns-grid" style={{
            gap: footer.columnGap ?? 48,
            justifyContent: footer.columnsAlign ?? 'space-between',
          }}>
            {columns.map((col, ci) => {
              const colAlign = normalizeAlign(col.align)
              return (
                <div
                  key={col.id ?? ci}
                  className="footer-col"
                  style={{
                    minWidth: col.minWidth ?? (col.type === 'company' ? 180 : 120),
                    textAlign: colAlign,
                  }}
                >
                  {col.type === 'company'
                    ? <CompanyColumn footer={footer} lang={lang} l={l} align={colAlign} />
                    : <LinksColumn col={col} footer={footer} l={l} />
                  }
                </div>
              )
            })}
          </div>

          {footer.showCopyright !== false && (
            <div style={{
              marginTop: 28, padding: '12px 0',
              borderTop: copyrightBorder ? `1px solid ${copyrightBorderColor}` : 'none',
              fontSize: copyrightSize, color: copyrightColor, textAlign: 'center',
            }}>
              {footer.copyrightText?.[l] || footer.copyrightText?.en
                ? <>{footer.copyrightText?.[l] || footer.copyrightText?.en} © {new Date().getFullYear()} · Powered by{' '}
                    <Link href="https://welist.ai" target="_blank" style={{ color:'inherit' }}>WeList.ai</Link></>
                : <>{companyName ? `${companyName} — ` : ''}© {new Date().getFullYear()} · Powered by{' '}
                    <Link href="https://welist.ai" target="_blank" style={{ color:'inherit' }}>WeList.ai</Link>
                  </>
              }
            </div>
          )}
        </div>
      </footer>
    </>
  )
}

export default FooterDefault